import React, { FC, useMemo } from 'react';
import { Document, Font, Image, Link, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import {
  Education,
  ProfessionalExperience,
  References,
  VolunteerExperience,
} from '../../../models/UserModels';
import moment from 'moment';
import { PdfHtml } from '../PdfHtml';
import Utils from '../../../utils/Utils';
import { CvTemplateProfile, ProfileActivity } from '../CvTemplateModal';
import config from '../../../config';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Font.register(Utils.getRubikFont());
Font.registerHyphenationCallback((word: string) => {
  const urlIndex = word.indexOf('://');
  if (urlIndex !== -1) {
    const schema = word.substring(0, urlIndex + 3);
    const urlText = word
      .substring(urlIndex + 3)
      .split('/')
      .map((v, i) => (i === 0 ? `${schema}${v}` : v));
    return urlText
      .map((v, i) => (i === urlText.length - 1 ? v : `${v}/`))
      .flatMap((v) => v.match(/(.{1,18})/g))
      .filter((v) => !!v) as string[];
  }
  return (word.match(/(.{1,18})/g) as string[]).filter((v) => !!v) as string[];
});
const colors = (color: string) => {
  let color1;
  let color2;
  let color3;
  if (color === '#14ae9f') {
    color1 = '#05756a';
    color2 = '#54c4c7';
    color3 = '#384347';
  }
  if (color === '#468f9a') {
    color1 = '#255f69';
    color2 = '#54c4c7';
    color3 = '#384347';
  }
  if (color === '#9dd9b8') {
    color1 = '#488262';
    color2 = '#39bd74';
    color3 = '#384347';
  }
  if (color === '#38b8ba') {
    color1 = '#1b8082';
    color2 = '#54c4c7';
    color3 = '#384347';
  }
  if (color === '#006666') {
    color1 = '#004747';
    color2 = '#54c4c7';
    color3 = '#384347';
  }
  if (color === '#49308b') {
    color1 = '#2c1a5c';
    color2 = '#764fdb';
    color3 = '#384347';
  }
  return { color1, color2, color3 };
};
const pageStyles = (props?: any) =>
  StyleSheet.create({
    body: {
      // flex: 1,
      // flexDirection: 'row',
      fontFamily: 'Rubik',
      paddingBottom: 24,
    },
    contentWrapper: {
      flex: 1,
      flexDirection: 'column',
    },
    iconText: {
      fontSize: 9,
      fontWeight: 'light',
      flexWrap: 'wrap',
    },
    headerName: {
      color: props?.color ? colors(props.color).color3 : '#384347',
      fontSize: 24,
      fontWeight: 'normal',
      marginTop: 40,
    },
    headerStatement: {
      color: props?.color ? colors(props.color).color2 : '#54c4c7',
      fontSize: 12,
      fontWeight: 'normal',
    },
    summary: {
      paddingTop: 10,
    },
    contacts: {
      paddingVertical: 6,
      flexDirection: 'row',
      justifyContent: 'flex-start',
      flexWrap: 'wrap',
      fontSize: 8,
    },
    row: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
      paddingRight: 12,
    },
    headerContactText: {
      fontSize: 9,
      marginLeft: 3,
      fontWeight: 'normal',
      textDecoration: 'none',
      color: '#384347',
    },
    titleText: {
      fontSize: 12,
      fontWeight: 'normal',
      color: '#ffffff',
      borderBottomWidth: 1,
      paddingBottom: 5,
    },
    designation: {
      fontSize: 11,
      fontWeight: 'normal',
      color: props?.color ? colors(props.color).color3 : '#384347',
    },
    designationWhite: {
      fontSize: 12,
      fontWeight: 'medium',
      color: '#ffffff',
    },
    company: {
      fontSize: 11,
      fontWeight: 'normal',
      marginTop: 2,
      color: props?.color ? colors(props.color).color2 : '#54c4c7',
    },
    companyWhite: {
      fontSize: 12,
      marginTop: 1,
      fontWeight: 'light',
      color: '#ffffff',
    },
    lang: {
      fontSize: 11,
      fontWeight: 'normal',
      color: '#ffffff',
    },
    fluency: {
      fontSize: 10,
      marginTop: 1,
      fontWeight: 'light',
      color: '#ffffff',
    },
    durationCountryWrapper: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    country: {
      fontSize: 9,
      fontWeight: 'light',
      color: props?.color ? props.color : '#43959b',
      marginLeft: 10,
      fontStyle: 'italic',
    },
    duration: {
      fontSize: 9,
      fontWeight: 'light',
      color: props?.color ? props.color : '#43959b',
      fontStyle: 'italic',
    },
    dottedLine: {
      marginTop: 10,
      borderBottomWidth: 1,
      borderBottomColor: props?.color ? colors(props.color).color2 : '#E0E0E0',
      borderStyle: 'dashed',
    },
    resultsWrapper: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 5,
    },
    results: {
      fontSize: 10,
      fontWeight: 'normal',
      color: props?.color ? colors(props.color).color3 : '#6F6F6F',
    },
    languages: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      gap: 15,
      marginTop: 10,
    },
    skillWrapper: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      rowGap: 6,
      marginTop: 10,
    },
    skills: {
      fontSize: 10,
      fontWeight: 'normal',
      color: '#ffffff',
    },
    skillDot: {
      width: 4,
      height: 4,
      borderRadius: '50%',
      backgroundColor: '#ffffff',
      marginHorizontal: 4,
    },
    refName: {
      fontSize: 12,
      fontWeight: 'medium',
      color: '#ffffff',
    },
    refDesignation: {
      fontSize: 10,
      marginTop: 2,
      fontWeight: 'normal',
      color: '#ffffff',
    },
    refOrganization: {
      fontSize: 9,
      fontWeight: 'normal',
      marginTop: 2,
      color: '#ffffff',
    },
    refContact: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      marginTop: 3,
      gap: 3,
    },
    refContactText: {
      fontSize: 9,
      fontWeight: 'light',
      flexWrap: 'wrap',
      color: '#ffffff',
      opacity: 0.8,
    },
    content: {
      flexDirection: 'row',
    },
    leftColumn: {
      width: 210,
      flexDirection: 'column',
      backgroundColor: 'transparent',
    },
    leftColumnContent: {
      paddingHorizontal: 24,
      flexDirection: 'column',
    },
    rightColumn: {
      flexDirection: 'column',
      width: 384,
      paddingHorizontal: 24,
    },
    sideBar: {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      width: 210,
      backgroundColor: props?.color && props.color === '#9dd9b8' ? '#67a382' : props?.color,
    },
    topBar: {
      height: 10,
      backgroundColor: props?.color ? colors(props.color).color1 : '#004747',
    },
    imageWrapper: {
      width: 'auto',
      flexDirection: 'row',
      justifyContent: 'center',
      marginVertical: 24,
    },
    footer: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      height: 24,
      zIndex: 100,
    },
  });

// Create Document Component
type Props = {
  selectedColor?: string;
  profile: CvTemplateProfile;
};
const ExpCard: FC<{ value: ProfessionalExperience; selectedColor?: string; isLast?: boolean }> = ({
  value,
  selectedColor,
  isLast,
}) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const duration = useMemo(() => {
    const from = moment(value.period.from);
    const fromStr = from.format('MMM YYYY');
    const to = moment(value.period.to);
    const toStr = value.period.to ? to.format('MMM YYYY') : 'Present';
    return `${fromStr} - ${toStr}`;
  }, [value.period.from, value.period.to]);
  return (
    <View wrap={false} style={{ marginTop: 10 }}>
      <View style={styles.durationCountryWrapper}>
        <Text style={styles.designation}>{value.designation.value}</Text>
        <IconTextView
          text={duration}
          img={`${config.frontendUrl}/calendarLine.png`}
          selectedColor={colors(selectedColor ?? '#006666').color3}
          isIcon={false}
        />
      </View>
      <View style={[styles.durationCountryWrapper, { marginBottom: 5 }]}>
        <Text style={styles.company}>{value.company.value}</Text>
        <IconTextView
          text={value.country || ''}
          img={`${config.frontendUrl}/locationLine.png`}
          selectedColor={colors(selectedColor ?? '#006666').color3}
          isIcon={false}
        />
      </View>
      {value.description && (
        <DescriptionText text={value.description} fontSize="9" lineHeight={1.4} />
      )}
      {!isLast && <Text />}
    </View>
  );
};
const VolCard: FC<{ value: VolunteerExperience; selectedColor?: string; isLast?: boolean }> = ({
  value,
  selectedColor,
  isLast,
}) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const duration = useMemo(() => {
    const from = moment(value.period.from);
    const fromStr = from.format('MMM YYYY');
    const to = moment(value.period.to);
    const toStr = value.period.to ? to.format('MMM YYYY') : 'Present';
    return `${fromStr} - ${toStr}`;
  }, [value.period.from, value.period.to]);
  return (
    <View wrap={false} style={{ marginTop: 10 }}>
      <View style={[styles.durationCountryWrapper, { marginBottom: 5, alignItems: 'center' }]}>
        <View style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
          <Text style={styles.designation}>{value.organization.value}</Text>
          <Text style={styles.company}>{value.role.value}</Text>
        </View>
        <IconTextView
          text={duration}
          img={`${config.frontendUrl}/calendarLine.png`}
          selectedColor={colors(selectedColor ?? '#006666').color3}
          isIcon={false}
        />
      </View>
      {value.description && (
        <DescriptionText text={value.description} fontSize="9" lineHeight={1.4} />
      )}
      {!isLast && <Text />}
    </View>
  );
};
const EduCard: FC<{ value: Education; selectedColor?: string; isLast?: boolean }> = ({
  value,
  selectedColor,
  isLast,
}) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const { type, results, institute, achievements, title } = value;
  const duration = useMemo(() => {
    const from = moment(value.period?.from);
    const fromStr = from.format('MMM YYYY');
    const to = moment(value.period?.to);
    const toStr = value?.period?.to
      ? to.format('MMM YYYY')
      : type === 'Certification'
      ? 'None'
      : 'Present';
    return [`${fromStr} - ${toStr}`, fromStr, toStr];
  }, [type, value?.period?.from, value?.period?.to]);
  return (
    <View wrap={false} style={{ marginTop: 10 }}>
      <View>
        {type === 'Certification' ? (
          <>
            <Text style={styles.designation}>{`${results[0]?.subject?.value ?? type}`}</Text>
          </>
        ) : type === 'GCE A/L' || type === 'GCE O/L' ? (
          <>
            <Text style={styles.designation}>{type}</Text>
            <View style={styles.resultsWrapper}>
              {results.map((r, i) => (
                <Text key={i} style={styles.results}>{`${r.subject?.value} ${
                  r.result ? `(${r.result}),` : ','
                }`}</Text>
              ))}
            </View>
          </>
        ) : type === 'Other' ? (
          <>
            <Text style={styles.designation}>{title ?? ''}</Text>
            <View style={styles.resultsWrapper}>
              {results.map((r, i) => (
                <Text key={i} style={styles.results}>{`${r.subject?.value} ${
                  r.result ? `(${r.result})` : ''
                }`}</Text>
              ))}
            </View>
          </>
        ) : (
          results.map((r, i) => (
            <Text key={i} style={styles.designation}>{`${r.subject?.value}${
              r.result ? `, ${r.result}` : ''
            }`}</Text>
          ))
        )}
      </View>
      {institute && <Text style={styles.company}>{institute.value}</Text>}
      {type !== 'Certification' && (
        <View style={[styles.durationCountryWrapper, { marginBottom: 5 }]}>
          <IconTextView
            text={duration[0]}
            img={`${config.frontendUrl}/calendarLine.png`}
            selectedColor={selectedColor}
            isIcon={false}
          />
          <IconTextView
            text={value.country || ''}
            img={`${config.frontendUrl}/locationLine.png`}
            selectedColor={selectedColor}
            isIcon={false}
          />
        </View>
      )}
      {type === 'Certification' && value?.period && (
        <View style={[styles.durationCountryWrapper, { marginBottom: 5 }]}>
          <IconTextView
            text={duration[1]}
            img={`${config.frontendUrl}/calendarLine.png`}
            selectedColor={colors(selectedColor ?? '#006666').color2}
            isIcon={false}
          />
          <IconTextView
            text={value.country || ''}
            img={`${config.frontendUrl}/locationLine.png`}
            selectedColor={colors(selectedColor ?? '#14ae9f').color2}
            isIcon={false}
          />
        </View>
      )}
      {value.description && (
        <DescriptionText text={value.description} fontSize="9" lineHeight={1.4} />
      )}
      {achievements && <DescriptionText text={achievements} fontSize="9" lineHeight={1.4} />}
      {!isLast && <Text />}
    </View>
  );
};
const ActivitySocityView: FC<{
  value: ProfileActivity[];
  selectedColor?: string;
}> = ({ value, selectedColor }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const activity = value ?? [];
  const data = activity.map((item, index) => {
    const isLastActivity = index === activity.length - 1;
    return (
      <View wrap={false} key={index}>
        {index === 0 && (
          <Text style={[styles.designationWhite, { marginTop: 10 }]}>Activities & Societies</Text>
        )}
        {item.institute && item.description && (
          <Text style={[styles.companyWhite, { marginTop: 6 }]}>{item.institute}</Text>
        )}

        {item.description.map((v) => {
          if (v) {
            return (
              <View wrap={false} style={{ marginTop: 4 }} key={v}>
                <DescriptionText text={v} fontSize="9" lineHeight={1.4} color="#ffffff" />
              </View>
            );
          }
        })}
        {!isLastActivity && <Text />}
      </View>
    );
  });
  return <>{data}</>;
};
const ContactDetailView: FC<{
  img: string;
  value: string;
  selectedColor?: string;
  isUrl?: boolean;
  title?: string;
  type?: 'email';
  iconWidth?: number;
  iconHeight?: number;
  width?: string;
}> = ({ selectedColor, img, value, isUrl, title, type, iconHeight, iconWidth, width }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View style={[styles.row, { width: width, marginRight: 4 }]}>
      <Image
        src={img}
        style={{
          width: iconWidth ?? 10,
          height: iconHeight ?? 10,
          marginRight: 1,
          color: selectedColor,
          padding: 1,
          opacity: 0.5,
        }}
      />
      <View style={{ paddingVertical: 2 }}>
        {isUrl ? (
          <Link wrap style={styles.headerContactText} src={value}>
            {title ?? value}
          </Link>
        ) : type && type === 'email' ? (
          <Link wrap style={styles.headerContactText} src={value}>
            {title ?? value}
          </Link>
        ) : (
          <Text style={styles.headerContactText}>{value}</Text>
        )}
      </View>
    </View>
  );
};
const ReferenceView: FC<{
  value: References;
  selectedColor?: string;
  isLast?: boolean;
}> = ({ value, selectedColor, isLast }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View wrap={false} style={{ marginTop: 10 }}>
      <Text style={styles.refName}>
        {value.firstName} {value.lastName}
      </Text>
      <Text style={styles.refDesignation}>{value.designation}</Text>
      <Text style={styles.refOrganization}>{value.organization}</Text>
      <View style={styles.refContact}>
        <Text style={styles.refContactText}>{value.email}</Text>
        <Text style={styles.refContactText}>{value.mobile}</Text>
      </View>
      {!isLast && <Text />}
    </View>
  );
};

const TitleText: FC<{
  title: string;
  selectedColor?: string;
  color?: string;
  borderColor?: string;
}> = ({ title, selectedColor, color, borderColor }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <Text
      style={[
        styles.titleText,
        {
          color: color,
          borderBottomColor: borderColor ? borderColor : '#ffffff',
        },
      ]}>
      {title.toUpperCase()}
    </Text>
  );
};

const DescriptionText: FC<{
  text: string;
  color?: string;
  fontSize?: string;
  fontFamily?: string;
  fontWeight?: string;
  lineHeight?: number;
}> = ({ text, color, fontSize, fontFamily, fontWeight, lineHeight }) => {
  return (
    <PdfHtml
      level={2}
      fontSize={fontSize}
      textColor={color}
      fontFamily={fontFamily ? fontFamily : 'Rubik'}
      fontWeight={fontWeight}
      lineHeight={lineHeight}>
      {text.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}
    </PdfHtml>
  );
};
const IconTextView: FC<{
  text: string;
  img: string;
  isIcon: boolean;
  selectedColor?: string;
  iconWidth?: number;
  iconHeight?: number;
}> = ({ text, img, selectedColor, iconWidth, iconHeight, isIcon }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View style={styles.row}>
      {isIcon && (
        <Image
          src={img}
          style={{
            width: iconWidth ?? 10,
            height: iconHeight ?? 10,
            marginRight: 2,
            color: colors(selectedColor ?? '#14ae9f').color2,
            padding: 1,
          }}
        />
      )}
      <Text style={[styles.iconText, { color: selectedColor }]}>{text}</Text>
    </View>
  );
};

const Template6 = (props: Props) => {
  const newColor = props.selectedColor ? props.selectedColor : '#006666';
  const styles = useMemo(() => pageStyles({ color: newColor }), [newColor]);
  const { profile } = props;

  const isShowActivity = useMemo(() => {
    const temp: Education[] = [...(profile?.education ?? [])];
    const found = temp.find((v) => v.description);
    return !!found;
  }, [profile?.education]);
  const isShowSummery = useMemo(() => {
    return !!(profile?.personalStatement || profile?.desiredJobTitle);
  }, [profile?.desiredJobTitle, profile?.personalStatement]);
  return (
    <Document>
      <Page size={'A4'} break={true} style={styles.body}>
        <View fixed style={styles.sideBar} />
        {/* HEADER SECTION */}
        {/* <View style={styles.header}>

          
        </View> */}
        <View style={styles.content}>
          <View style={styles.leftColumn}>
            <View style={styles.topBar} />
            <View style={styles.leftColumnContent}>
              <View
                fixed
                render={({ pageNumber }) => {
                  if (pageNumber !== 1) {
                    return <View style={{ height: 24 }} />;
                  }
                  return <View />;
                }}
              />
              <View style={styles.imageWrapper}>
                {profile?.picture ? (
                  <Image
                    style={{ width: 70, height: 70, borderRadius: '5%', objectFit: 'cover' }}
                    src={profile.picture ? profile.picture : config.frontendUrl + '/userImage.jpg'}
                  />
                ) : (
                  <></>
                )}
              </View>

              {/* ACTIVITIES & SOCIETIES SECTION */}
              {isShowActivity && (
                <View style={{ marginBottom: 15 }}>
                  <TitleText title="Achievements" selectedColor={newColor} />
                  <ActivitySocityView value={profile.activities} selectedColor={newColor} />
                </View>
              )}
              {/* SKILLS SECTION */}
              {profile?.skills && profile.skills.length > 0 && (
                <View wrap={false} style={{ marginBottom: 15 }}>
                  <TitleText title="Skills" selectedColor={newColor} />
                  <View style={styles.skillWrapper}>
                    {profile.skills.map((skill, index) => (
                      <View key={index} style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <Text style={styles.skills}>{skill.value}</Text>
                        <View style={styles.skillDot} />
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* LANGUAGES SECTION */}
              {profile?.languages && profile.languages.length > 0 && (
                <View wrap={false} style={{ marginBottom: 15 }}>
                  <TitleText title="Languages" selectedColor={newColor} />
                  <View style={styles.languages}>
                    {profile.languages.map((lang, index) => (
                      <View
                        key={index}
                        style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Text style={styles.lang}>{lang.language}</Text>
                        <Text style={styles.fluency}>{lang.fluency}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* REFERENCES SECTION */}
              {profile?.reference && profile.reference.length > 0 && (
                <View style={{ marginBottom: 15 }}>
                  <TitleText title="References" selectedColor={newColor} />
                  {profile.reference.map((ref, index) => (
                    <ReferenceView
                      key={index}
                      value={ref}
                      selectedColor={colors(newColor).color1}
                      isLast={profile.reference ? index === profile.reference.length - 1 : false}
                    />
                  ))}
                </View>
              )}
            </View>
            <View fixed style={styles.footer} />
          </View>
          <View style={styles.rightColumn}>
            <View
              fixed
              render={({ pageNumber }) => {
                if (pageNumber !== 1) {
                  return <View style={{ height: 24 }} />;
                }
                return <View />;
              }}
            />
            <View>
              <Text style={styles.headerName}>{profile.name}</Text>
              <Text style={styles.headerStatement}>{profile.personalStatementHeader}</Text>
            </View>

            {/* CONTACTS */}
            <View style={styles.contacts}>
              {profile?.mobile && (
                <ContactDetailView
                  value={profile.mobile}
                  img={`${config.frontendUrl}/phone.png`}
                  selectedColor={colors(newColor).color3}
                />
              )}
              {profile?.residentialAddress && (
                <ContactDetailView
                  value={profile.residentialAddress}
                  img={`${config.frontendUrl}/locationPin.png`}
                  selectedColor={colors(newColor).color3}
                />
              )}
              {profile?.linkedInUrl && (
                <ContactDetailView
                  isUrl={true}
                  title={'Linkedin.com'}
                  value={profile.linkedInUrl}
                  img={`${config.frontendUrl}/linkedin.png`}
                  selectedColor={colors(newColor).color3}
                />
              )}
              {profile?.userId && (
                <ContactDetailView
                  isUrl={true}
                  title={'Careers360.com'}
                  value={`${config.frontendUrl}/profile/${profile.userId}`}
                  img={`${config.frontendUrl}/careers360.png`}
                  selectedColor={colors(newColor).color3}
                />
              )}
              {profile?.email && (
                <ContactDetailView
                  title={profile.email}
                  type={'email'}
                  value={`mailto:${profile.email}`}
                  img={`${config.frontendUrl}/email.png`}
                  selectedColor={colors(newColor).color3}
                />
              )}
            </View>

            {isShowSummery && (
              <View style={{ marginBottom: 15, marginTop: 24 }}>
                <TitleText
                  title="Summary"
                  selectedColor={newColor}
                  color={colors(newColor).color3}
                  borderColor="#dadbdb"
                />

                <View style={styles.summary}>
                  {profile.personalStatement && (
                    <DescriptionText
                      text={profile.personalStatement}
                      fontSize="9"
                      lineHeight={1.3}
                    />
                  )}
                </View>
              </View>
            )}

            {/* EXPERIENCE SECTION */}
            {profile.experience && profile.experience.length > 0 && (
              <View style={{ marginBottom: 15 }}>
                <TitleText
                  title="Work Experience"
                  selectedColor={newColor}
                  color={colors(newColor).color3}
                  borderColor="#dadbdb"
                />
                {profile.experience.map((exp, index) => (
                  <ExpCard
                    key={index}
                    value={exp}
                    selectedColor={newColor}
                    isLast={profile.experience ? index === profile.experience.length - 1 : false}
                  />
                ))}
              </View>
            )}
            {/* EDUCATION SECTION */}
            {profile.education && profile.education.length > 0 && (
              <View style={{ marginBottom: 15 }}>
                <TitleText
                  title="Education"
                  selectedColor={newColor}
                  color={colors(newColor).color3}
                  borderColor="#dadbdb"
                />
                {profile.education.map((edu, index) => (
                  <EduCard
                    key={index}
                    value={edu}
                    selectedColor={newColor}
                    isLast={profile.education ? index === profile.education.length - 1 : false}
                  />
                ))}
              </View>
            )}
            {/* VOLUNTEER SECTION */}
            {profile?.volunteering && profile.volunteering.length > 0 && (
              <View style={{ marginBottom: 15 }}>
                <TitleText
                  title="Volunteering"
                  selectedColor={newColor}
                  color={colors(newColor).color3}
                  borderColor="#dadbdb"
                />
                {profile.volunteering.map((vol, index) => (
                  <VolCard
                    key={index}
                    value={vol}
                    selectedColor={newColor}
                    isLast={
                      profile.volunteering ? index === profile.volunteering.length - 1 : false
                    }
                  />
                ))}
              </View>
            )}
            <View fixed style={styles.footer} />
          </View>
        </View>
      </Page>
    </Document>
  );
};
export default Template6;
